import React, { useState } from 'react';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';
import { CircularProgress } from '@mui/material';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useParams } from 'react-router-dom';
import { useConnectionsConfiguration } from 'apps/sigma2-ui/src/lib/hooks/useConnectionsConfiguration';
import { ConnectionsConfiguration } from 'libs/end-to-end/src/connections/connections-config';
import { Switch } from 'apps/sigma2-ui/src/components/ui/Switch';
import { TextInput } from 'apps/sigma2-ui/src/components/ui/TextInput';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { PrimaryButtonLightBg } from 'apps/sigma2-ui/src/components/PrimaryButtonLightBg';
import { useMemo } from 'react';
import { useOnChange } from 'apps/sigma2-ui/src/lib/hooks/use-on-change';

export const OrganizationConnectionsConfigurationPage = () => {
  const language = useLanguage();
  const { organizationId } = useParams();

  const connections = useConnectionsConfiguration(organizationId ?? undefined);

  const { state, config, errorMessage, createOrUpdate } = connections;

  const [editingConfig, setEditingConfig] = useState<ConnectionsConfiguration>({
    isEnabled: false,
  });

  const editingConfigState = useMemo(() => {
    return {
      state,
      config,
    };
  }, [state, config]);

  useOnChange(editingConfigState, ({ current }) => {
    if (current.state === 'succeeded' && current.config) {
      setEditingConfig(current.config);
    }
  });

  if (!organizationId) {
    return (
      <AdminLayout currentPage={'OrganizationConnectionsConfiguration'}>
        <S2Typography
          variant="headline-4"
          translatedContent="Organization not found"
        />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout currentPage="OrganizationConnectionsConfiguration">
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'Organizations', to: '/organizations' },
          {
            label: language.manageOrganizationConnectionsPageTitle(),
            to: `/organizations/${organizationId}/connections`,
          },
        ]}
      />

      <Box as={'div'} display={'flex'} alignItems={'center'}>
        <Spacing size="md" direction="vertical">
          <S2Typography
            variant="headline-4"
            translatedContent={language.manageOrganizationConnectionsPageTitle()}
          />

          {state === 'loading' && <CircularProgress />}

          {state === 'failed' && (
            <S2Typography
              variant="body-6"
              textColorVariant="red-500"
              translatedContent={errorMessage ?? language.unknownError()}
            />
          )}

          {state === 'succeeded' && config && (
            <Spacing size="sm" direction="vertical">
              <Switch
                checked={editingConfig.isEnabled}
                onChange={() => {
                  setEditingConfig(
                    editingConfig.isEnabled
                      ? {
                          isEnabled: false,
                        }
                      : {
                          isEnabled: true,
                          connections: {
                            slack: {
                              isEnabled: false,
                            },
                            teams: {
                              isEnabled: false,
                            },
                            greenhouse: {
                              isEnabled: false,
                            },
                            lever: {
                              isEnabled: false,
                            },
                            fountain: {
                              isEnabled: false,
                            },
                          },
                        },
                  );
                }}
              >
                {editingConfig.isEnabled ? 'Disable' : 'Enable'} Connections
              </Switch>
              {editingConfig.isEnabled && (
                <>
                  <Spacing size="gap-1" direction="vertical">
                    <S2Typography
                      variant="headline-5"
                      translatedContent={language.connectionsPageSlackHeading()}
                    />

                    <Switch
                      checked={editingConfig.connections.slack.isEnabled}
                      onChange={() => {
                        setEditingConfig(
                          editingConfig.connections.slack.isEnabled
                            ? {
                                isEnabled: true,
                                connections: {
                                  ...editingConfig.connections,
                                  slack: {
                                    isEnabled: false,
                                  },
                                },
                              }
                            : {
                                isEnabled: true,
                                connections: {
                                  ...editingConfig.connections,
                                  slack: {
                                    isEnabled: true,
                                  },
                                },
                              },
                        );
                      }}
                    >
                      {editingConfig.connections.slack.isEnabled
                        ? 'Disable'
                        : 'Enable'}{' '}
                      Slack Connection
                    </Switch>

                    <hr className="border-0 border-t border-[#E7E8E8] my-1" />
                  </Spacing>

                  <Spacing size="gap-1" direction="vertical">
                    <S2Typography
                      variant="headline-5"
                      translatedContent="Greenhouse"
                    />

                    <Switch
                      checked={editingConfig.connections.greenhouse.isEnabled}
                      onChange={() => {
                        setEditingConfig(
                          editingConfig.connections.greenhouse.isEnabled
                            ? {
                                isEnabled: true,
                                connections: {
                                  ...editingConfig.connections,
                                  greenhouse: {
                                    isEnabled: false,
                                  },
                                },
                              }
                            : {
                                isEnabled: true,
                                connections: {
                                  ...editingConfig.connections,
                                  greenhouse: {
                                    isEnabled: true,
                                  },
                                },
                              },
                        );
                      }}
                    >
                      {editingConfig.connections.greenhouse.isEnabled
                        ? 'Disable'
                        : 'Enable'}{' '}
                      Greenhouse Connection
                    </Switch>

                    <hr className="border-0 border-t border-[#E7E8E8] my-1" />
                  </Spacing>

                  <Spacing size="gap-1" direction="vertical">
                    <S2Typography
                      variant="headline-5"
                      translatedContent="Lever"
                    />

                    <Switch
                      checked={editingConfig.connections.lever.isEnabled}
                      onChange={() => {
                        setEditingConfig(
                          editingConfig.connections.lever.isEnabled
                            ? {
                                isEnabled: true,
                                connections: {
                                  ...editingConfig.connections,
                                  lever: {
                                    isEnabled: false,
                                  },
                                },
                              }
                            : {
                                isEnabled: true,
                                connections: {
                                  ...editingConfig.connections,
                                  lever: {
                                    isEnabled: true,
                                  },
                                },
                              },
                        );
                      }}
                    >
                      {editingConfig.connections.lever.isEnabled
                        ? 'Disable'
                        : 'Enable'}{' '}
                      Lever Connection
                    </Switch>

                    <hr className="border-0 border-t border-[#E7E8E8] my-1" />
                  </Spacing>

                  <Spacing size="gap-1" direction="vertical">
                    <S2Typography
                      variant="headline-5"
                      translatedContent="Fountain"
                    />

                    <Switch
                      checked={editingConfig.connections.fountain.isEnabled}
                      onChange={() => {
                        setEditingConfig(
                          editingConfig.connections.fountain.isEnabled
                            ? {
                                isEnabled: true,
                                connections: {
                                  ...editingConfig.connections,
                                  fountain: {
                                    isEnabled: false,
                                  },
                                },
                              }
                            : {
                                isEnabled: true,
                                connections: {
                                  ...editingConfig.connections,
                                  fountain: {
                                    isEnabled: true,
                                  },
                                },
                              },
                        );
                      }}
                    >
                      {editingConfig.connections.fountain.isEnabled
                        ? 'Disable'
                        : 'Enable'}{' '}
                      Fountain Connection
                    </Switch>

                    {editingConfig.connections.fountain.isEnabled && (
                      <TextInput
                        type="text"
                        data-qa="fountain-hire-api-base-url"
                        label="Fountain OAuth API base URL (services host)"
                        placeholder="https://services.fountain.com/api/serviceworkforce"
                        value={
                          editingConfig.connections.fountain.hireApiBaseUrl ??
                          ''
                        }
                        onChange={(e) => {
                          const v = e.currentTarget.value.trim();
                          setEditingConfig({
                            isEnabled: true,
                            connections: {
                              ...editingConfig.connections,
                              fountain: {
                                isEnabled: true,
                                hireApiBaseUrl: v.length > 0 ? v : undefined,
                              },
                            },
                          });
                        }}
                      />
                    )}

                    <hr className="border-0 border-t border-[#E7E8E8] my-1" />
                  </Spacing>
                </>
              )}
            </Spacing>
          )}
          <PrimaryButtonLightBg
            onClick={() => void createOrUpdate(editingConfig)}
            disabled={
              state === 'loading' || state === 'idle' || state === 'failed'
            }
            translatedContent={language.save()}
            icon={() => {
              if (state === 'loading') {
                return (
                  <CircularProgress
                    size={16}
                    thickness={4}
                    color={'inherit'}
                    className={
                      state === 'loading' || state === 'idle'
                        ? 'text-gray-400'
                        : 'text-violet-100'
                    }
                  />
                );
              }

              return null;
            }}
          />
        </Spacing>
      </Box>
    </AdminLayout>
  );
};
